import { createContext, SetStateAction, Dispatch, MutableRefObject } from 'react';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import type { AvatarConnectionState } from '../hooks/create-avatar-video-pipeline';

export const MAX_INACTIVITY_COUNT = 2;

// Shared by the handlers hook and countdown-display so they can't drift apart.
export const COUNTDOWN_DURATION_MS = 8000;

export enum AIRolePlayStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum Role {
  AI = 'ai',
  LEARNER = 'learner',
}

export enum MessageStatus {
  PENDING = 'pending',
  SENT = 'sent',
  FAILED = 'failed',
}
export enum AudioRecordingStatus {
  NOT_STARTED = 'not_started',
  RECORDING = 'recording',
  TRANSCRIPTING = 'transcripting',
}

export enum VoiceCardState {
  IDLE = 'idle',
  THINKING = 'thinking',
  SPEAKING = 'speaking',
}

export enum VoiceRecordingState {
  IDLE = 'idle',
  COUNTDOWN = 'countdown',
  RECORDING = 'recording',
  COMPLETED = 'completed',
}

export type Message = {
  id?: string;
  content: string;
  role: Role;
  createdAt?: Date;
  status?: MessageStatus;
};

export type SessionData = {
  aiApiEndpoint: string | null;
  jwtToken: string | null;
  submissionId: number | null;
  expiresAt: string | null;
  languageLocale: string | null;
};

export type AiRolePlaySubmissionPusherData = {
  userId: number;
  courseId: number;
  lecturePageId: number;
  aiRolePlayId: number;
  aiRolePlaySubmissionId: number;
  pointsReceived: number;
  finishedAt: string;
  leaderboardPoints: number;
  leaderboardRank: number;
  priorLeaderboardRank: number;
  closingReason?: string;
};
type AiConversationContextType = {
  interactionMode: RolePlayInteractionMode;
  aiRolePlayStatus: AIRolePlayStatus;
  setAiRolePlayStatus: (value: AIRolePlayStatus) => void;
  showScenarioDetails: boolean;
  setShowScenarioDetails: (value: boolean) => void;
  /**
   * Exits the chat. If skipConfirmation is true, exits immediately without
   * showing the confirmation dialog (used for timeout scenarios).
   */
  handleExitChat: (skipConfirmation?: boolean) => void;
  aiRolePlayId: number | null;
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  isTimeout: boolean;
  setIsTimeout: Dispatch<SetStateAction<boolean>>;
  /**
   * Ref to hold the AbortController for the current stream.
   * Used to abort the stream when exiting the chat.
   */
  abortControllerRef: MutableRefObject<AbortController | null>;
  isHardDeadlineExpired: boolean;
  isRolePlayStarting: boolean;
  setIsRolePlayStarting: Dispatch<SetStateAction<boolean>>;
  // Conversation handlers
  openingNarrative: string;
  handleChunk: (chunk: any) => void;
  handleError: () => void;
  handleStartConversation: () => void;
  /**
   * True while we are waiting for AI tokens to start arriving after the
   * opening narrative `done` event but before the first `delta` of the
   * first AI message. Used by the chat panel to render the typing UI
   * during this gap (the same UI shown while waiting on a learner-triggered reply).
   */
  isAwaitingAiResponse: boolean;
  /**
   * True while the conversation messages are being fetched (read-only
   * submission view). The live flow streams messages and leaves this unset.
   */
  isConversationLoading?: boolean;
  audioRecordingStatus?: AudioRecordingStatus;
  setAudioRecordingStatus?: Dispatch<SetStateAction<AudioRecordingStatus>>;
  microphoneEnabled?: boolean;
  // Voice mode card states
  aiVoiceCardState?: VoiceCardState;
  learnerVoiceCardState?: VoiceCardState;
  aiMediaStream?: MediaStream | null;
  learnerMediaStream?: MediaStream | null;
  voiceRecordingState?: VoiceRecordingState | null;
  /** True while the loop is paused after a mid-conversation stream error. */
  isStreamError?: boolean;
  /** Resumes the conversation loop after a mid-conversation stream error. */
  handleRestartFromStreamError?: () => void;
  inactivityCount?: number | null
  /** Video mode: binds the AI card's <video> element to the LiveKit avatar pipeline. */
  attachAvatarVideo?: (videoEl: HTMLVideoElement | null) => void;
  /** Video mode: LiveKit room connection state, null while no avatar session is live. */
  avatarConnectionState?: AvatarConnectionState | null;
};

const AiConversationContext = createContext<AiConversationContextType>({
  interactionMode: RolePlayInteractionMode.TEXT,
  aiRolePlayStatus: AIRolePlayStatus.NOT_STARTED,
  setAiRolePlayStatus: () => {},
  showScenarioDetails: true,
  setShowScenarioDetails: () => {},
  handleExitChat: () => {},
  aiRolePlayId: null,
  messages: [],
  setMessages: () => {},
  sessionData: {
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId: null,
    expiresAt: null,
    languageLocale: null,
  },
  setSessionData: () => {},
  isTimeout: false,
  setIsTimeout: () => {},
  abortControllerRef: { current: null },
  isHardDeadlineExpired: false,
  isRolePlayStarting: false,
  setIsRolePlayStarting: () => {},
  // Conversation handlers defaults
  openingNarrative: '',
  handleChunk: () => {},
  handleError: () => {},
  handleStartConversation: () => {},
  isAwaitingAiResponse: false,
  isConversationLoading: false,
  audioRecordingStatus: AudioRecordingStatus.NOT_STARTED,
  setAudioRecordingStatus: () => {},
  microphoneEnabled: false,
  aiVoiceCardState: VoiceCardState.IDLE,
  learnerVoiceCardState: VoiceCardState.IDLE,
  aiMediaStream: null,
  learnerMediaStream: null,
  voiceRecordingState: VoiceRecordingState.IDLE,
  isStreamError: false,
  handleRestartFromStreamError: () => {},
  inactivityCount: 0,
  attachAvatarVideo: () => {},
  avatarConnectionState: null,
});

export default AiConversationContext;
